import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useCreateEmployee, useUpdateEmployee, useDepartments, useEmployees } from '@/hooks/use-api'
import type { Employee, EmploymentType, EmploymentStatus, WorkLocation } from '@/types/hr'

interface EmployeeFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employee?: Employee | null
  onSaved?: (employee: Employee) => void
}

const EMPLOYMENT_TYPES: EmploymentType[] = ['Full-time', 'Part-time', 'Contractor', 'Intern']
const EMPLOYMENT_STATUSES: EmploymentStatus[] = ['Active', 'On Leave', 'Terminated']
const WORK_LOCATIONS: WorkLocation[] = ['Remote', 'Hybrid', 'On-site']

const EMPTY_FORM: Partial<Employee> = {
  first_name: '',
  last_name: '',
  preferred_name: '',
  pronouns: '',
  date_of_birth: null,
  personal_email: '',
  work_email: '',
  personal_phone: '',
  work_phone: '',
  job_title: '',
  department_id: null,
  manager_id: null,
  employment_type: null,
  employment_status: 'Active',
  hire_date: null,
  start_date: null,
  work_location: null,
  bio: '',
}

export function EmployeeForm({ open, onOpenChange, employee, onSaved }: EmployeeFormProps) {
  const { toast } = useToast()
  const createMutation = useCreateEmployee()
  const updateMutation = useUpdateEmployee()
  const { data: departments } = useDepartments()
  const { data: managerList } = useEmployees({ limit: 10000 })

  const [formData, setFormData] = useState<Partial<Employee>>(EMPTY_FORM)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormData(employee ? { ...employee } : EMPTY_FORM)
      setErrors({})
    }
  }, [open, employee])

  const isEdit = !!employee?.id

  const setField = <K extends keyof Employee>(key: K, value: Employee[K] | null) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
  }

  const validate = (): boolean => {
    const e: Record<string, string> = {}
    if (!formData.first_name?.trim()) e.first_name = 'First name is required'
    if (!formData.last_name?.trim()) e.last_name = 'Last name is required'

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (formData.work_email && !emailRegex.test(formData.work_email)) e.work_email = 'Invalid email'
    if (formData.personal_email && !emailRegex.test(formData.personal_email))
      e.personal_email = 'Invalid email'

    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!validate()) {
      toast({
        title: 'Validation error',
        description: 'Please fix the highlighted fields',
        variant: 'destructive',
      })
      return
    }

    // Convert empty strings to null for optional fields
    const payload: Partial<Employee> = { ...formData }
    Object.keys(payload).forEach((k) => {
      const key = k as keyof Employee
      if (payload[key] === '') (payload as Record<string, unknown>)[key] = null
    })

    try {
      const saved = isEdit
        ? await updateMutation.mutateAsync({ id: employee!.id, data: payload })
        : await createMutation.mutateAsync(payload)
      toast({ title: isEdit ? 'Employee updated' : 'Employee created' })
      onSaved?.(saved)
      onOpenChange(false)
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string; errors?: string[] } } })?.response
        ?.data
      toast({
        title: 'Error',
        description: msg?.errors?.join(', ') || msg?.error || 'Failed to save employee',
        variant: 'destructive',
      })
    }
  }

  const departmentOptions = departments ?? []
  const managerOptions = (managerList?.data ?? []).filter((m) => m.id !== employee?.id)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Employee' : 'New Employee'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the employee record.' : 'Add a new employee to the directory.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold">Identity</legend>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="emp-first-name">First name *</Label>
                <Input
                  id="emp-first-name"
                  value={formData.first_name ?? ''}
                  onChange={(e) => setField('first_name', e.target.value)}
                  aria-invalid={!!errors.first_name}
                  aria-describedby={errors.first_name ? 'emp-first-name-error' : undefined}
                />
                {errors.first_name && (
                  <p id="emp-first-name-error" className="text-xs text-destructive mt-1">
                    {errors.first_name}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="emp-last-name">Last name *</Label>
                <Input
                  id="emp-last-name"
                  value={formData.last_name ?? ''}
                  onChange={(e) => setField('last_name', e.target.value)}
                  aria-invalid={!!errors.last_name}
                  aria-describedby={errors.last_name ? 'emp-last-name-error' : undefined}
                />
                {errors.last_name && (
                  <p id="emp-last-name-error" className="text-xs text-destructive mt-1">
                    {errors.last_name}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="emp-preferred-name">Preferred name</Label>
                <Input
                  id="emp-preferred-name"
                  value={formData.preferred_name ?? ''}
                  onChange={(e) => setField('preferred_name', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="emp-pronouns">Pronouns</Label>
                <Input
                  id="emp-pronouns"
                  value={formData.pronouns ?? ''}
                  onChange={(e) => setField('pronouns', e.target.value)}
                  placeholder="e.g. she/her"
                />
              </div>
              <div>
                <Label htmlFor="emp-dob">Date of birth</Label>
                <DatePicker
                  id="emp-dob"
                  value={formData.date_of_birth}
                  onChange={(v) => setField('date_of_birth', v || null)}
                  aria-label="Date of birth"
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold">Contact</legend>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="emp-work-email">Work email</Label>
                <Input
                  id="emp-work-email"
                  type="email"
                  value={formData.work_email ?? ''}
                  onChange={(e) => setField('work_email', e.target.value)}
                  aria-invalid={!!errors.work_email}
                />
                {errors.work_email && (
                  <p className="text-xs text-destructive mt-1">{errors.work_email}</p>
                )}
              </div>
              <div>
                <Label htmlFor="emp-work-phone">Work phone</Label>
                <Input
                  id="emp-work-phone"
                  value={formData.work_phone ?? ''}
                  onChange={(e) => setField('work_phone', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="emp-personal-email">Personal email</Label>
                <Input
                  id="emp-personal-email"
                  type="email"
                  value={formData.personal_email ?? ''}
                  onChange={(e) => setField('personal_email', e.target.value)}
                  aria-invalid={!!errors.personal_email}
                />
                {errors.personal_email && (
                  <p className="text-xs text-destructive mt-1">{errors.personal_email}</p>
                )}
              </div>
              <div>
                <Label htmlFor="emp-personal-phone">Personal phone</Label>
                <Input
                  id="emp-personal-phone"
                  value={formData.personal_phone ?? ''}
                  onChange={(e) => setField('personal_phone', e.target.value)}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold">Employment</legend>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="emp-job-title">Job title</Label>
                <Input
                  id="emp-job-title"
                  value={formData.job_title ?? ''}
                  onChange={(e) => setField('job_title', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="emp-employee-number">Employee number</Label>
                <Input
                  id="emp-employee-number"
                  value={formData.employee_number ?? ''}
                  onChange={(e) => setField('employee_number', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="emp-department">Department</Label>
                <Select
                  value={formData.department_id ?? '__none'}
                  onValueChange={(v) => setField('department_id', v === '__none' ? null : v)}
                >
                  <SelectTrigger id="emp-department">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">—</SelectItem>
                    {departmentOptions.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="emp-manager">Manager</Label>
                <Select
                  value={formData.manager_id ?? '__none'}
                  onValueChange={(v) => setField('manager_id', v === '__none' ? null : v)}
                >
                  <SelectTrigger id="emp-manager">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">—</SelectItem>
                    {managerOptions.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.first_name} {m.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="emp-type">Employment type</Label>
                <Select
                  value={formData.employment_type ?? '__none'}
                  onValueChange={(v) =>
                    setField('employment_type', v === '__none' ? null : (v as EmploymentType))
                  }
                >
                  <SelectTrigger id="emp-type">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">—</SelectItem>
                    {EMPLOYMENT_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="emp-status">Employment status</Label>
                <Select
                  value={formData.employment_status ?? 'Active'}
                  onValueChange={(v) => setField('employment_status', v as EmploymentStatus)}
                >
                  <SelectTrigger id="emp-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYMENT_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="emp-hire-date">Hire date</Label>
                <DatePicker
                  id="emp-hire-date"
                  value={formData.hire_date}
                  onChange={(v) => setField('hire_date', v || null)}
                  aria-label="Hire date"
                />
              </div>
              <div>
                <Label htmlFor="emp-start-date">Start date</Label>
                <DatePicker
                  id="emp-start-date"
                  value={formData.start_date}
                  onChange={(v) => setField('start_date', v || null)}
                  aria-label="Start date"
                />
              </div>
              <div>
                <Label htmlFor="emp-work-location">Work location</Label>
                <Select
                  value={formData.work_location ?? '__none'}
                  onValueChange={(v) => setField('work_location', v === '__none' ? null : v)}
                >
                  <SelectTrigger id="emp-work-location">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">—</SelectItem>
                    {WORK_LOCATIONS.map((l) => (
                      <SelectItem key={l} value={l}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {isEdit ? 'Save changes' : 'Create employee'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
